import cn from 'classnames'
import { keyframes } from 'goober'
import * as Dialog from '@radix-ui/react-dialog'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Slider from '../Slider'
import PropTypes from 'prop-types'
import { createUseStyles } from '../../helpers/createStyles'
import useBreakpoint from '../../hooks/useBreakpoint'
import { theme } from '../../styles/theme'
import GalleryNavigation from './GalleryNavigation'
import GallerySlide from './GallerySlide'
import GalleryCursor from './GalleryCursor'
import GalleryFullscreen from './GalleryFullScreen'

import useSnapshot from '../../store/useSnapshot'
import { galleryIndex } from '../../store/layoutSlice'

const Gallery = ({ images, open, setOpen }) => {
  const styles = useStyles()
  const flick = useRef()
  const snap = useSnapshot()
  const { breakpoint } = useBreakpoint()
  const [zoomImage, setZoomImage] = useState(null)
  const _index = galleryIndex(snap)
  const [activeIndex, setActiveIndex] = useState(0)
  const imageLength = images?.length || 0

  // Gallery control handlers
  const handlePressPrev = useCallback(() => flick.current?.previous(), [])
  const handlePressNext = useCallback(() => flick.current?.next(), [])
  const handlePressSelect = useCallback(
    index => flick.current?.select(index),
    []
  )
  const handlePressZoom = useCallback(
    indexToZoom => setZoomImage(images[indexToZoom]),
    [images]
  )
  const handlePressClose = useCallback(() => setOpen(false), [setOpen])

  const handleSlideChange = useCallback(index => setActiveIndex(index), [])

  const isMobile = useMemo(
    () => ['min', 'xxs', 'xs', 'sm'].includes(breakpoint),
    [breakpoint]
  )

  const setFlickityRef = useCallback((flickref) => {
    flick.current = flickref
    flick.current?.select(_index, true)
  }, [_index])

  // Avoid re-rendering slider by passing in memoized options
  const options = useMemo(
    () => ({
      cellAlign: 'center',
      contain: false,
      draggable: isMobile,
      setGallerySize: false,
      on: {
        change: handleSlideChange
      }
    }),
    [handleSlideChange, isMobile]
  )

  useEffect(() => {
    setActiveIndex(0)
    setZoomImage(null)
  }, [open, options])

  const cursorRefs = useRef({}).current

  const setSlideRefs = useCallback(
    (slideIndex, slideRefs) => {
      if (slideIndex === activeIndex) {
        cursorRefs.activeImage = slideRefs.image
        cursorRefs.activeSlide = slideRefs.root
      } else if (slideIndex + 1 === activeIndex) {
        cursorRefs.prevSlide = slideRefs.root
      } else if (slideIndex - 1 === activeIndex) {
        cursorRefs.nextSlide = slideRefs.root
      }

      if (activeIndex === 0) {
        cursorRefs.prevSlide = null
      } else if (activeIndex === imageLength - 1) {
        cursorRefs.nextSlide = null
      }
    },
    [activeIndex, imageLength]
  )

  return (
    <Dialog.Root modal open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Content
          className={styles.content}
          aria-label='Product image gallery'
        >
          <Slider
            className={styles.slider}
            key={JSON.stringify({ options, open })}
            setFlickityRef={setFlickityRef}
            options={options}
          >
            {images?.map((image, index) => (
              <GallerySlide
                image={image}
                key={index}
                index={index}
                activeIndex={activeIndex}
                onSelect={handlePressSelect}
                onClose={handlePressClose}
                onZoom={handlePressZoom}
                setRefs={setSlideRefs}
              />
            ))}
          </Slider>
          {isMobile && (
            <GalleryNavigation
              handlePressNext={handlePressNext}
              handlePressPrev={handlePressPrev}
              nextDisabled={activeIndex >= imageLength - 1}
              prevDisabled={activeIndex === 0}
            />
          )}
          {!isMobile && (
            <>
              <GalleryCursor refs={cursorRefs} open={open} />
              <Dialog.Close
                className={cn(styles.close, styles.closeLeft, {
                  [styles.closeDisabled]: activeIndex > 0
                })}
              />
              <Dialog.Close
                className={cn(styles.close, styles.closeRight, {
                  [styles.closeDisabled]: activeIndex < imageLength - 1
                })}
              />
              <GalleryFullscreen
                image={zoomImage}
                onClose={() => setZoomImage(null)}
              />
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

const contentShow = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const useStyles = createUseStyles({
  content: {
    '--gutter': '24px',
    '--box-width': 'calc(min(100vw, 100vh) - (var(--gutter) * 2))',
    '--peek': '24px',
    width: '100vw',
    height: '100vh',
    background: theme.colors.black,
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    zIndex: theme.zIndex.gallery,
    [theme.breakpoints.up('sm')]: {
      '--box-width': 'min(60vw, 100vh)',
      cursor: 'none'
    },
    '& .flickity-viewport': {
      height: '100%'
    },
    '& .flickity-slider': {
      height: '100%'
    },
    animation: `${contentShow} 150ms ${theme.eases.easeInOut}`
  },
  slider: {
    [theme.breakpoints.down('sm')]: {
      height: 'calc(100% - 120px)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 120
    }
  },
  close: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 'calc(((100vw - var(--box-width)) / 4) + var(--peek) / 2)',
    cursor: 'none!important'
  },
  closeLeft: {
    left: 0
  },
  closeRight: {
    right: 0
  },
  closeDisabled: {
    pointerEvents: 'none',
    display: 'none'
  }
})

Gallery.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired
}

export default Gallery
